import { createSlice } from '@reduxjs/toolkit';
import lodashFindIndex from 'lodash/findIndex';
import lodashIsEmpty from 'lodash/isEmpty';
import lodashIsUndefined from 'lodash/isUndefined';

import { formikNames } from '../../Components/Reservation/constants';
import constants from '../../Config/constants';

const gettingUserDataInitialState = {
  loading: true, // default to true, since user data must be fetch first
  error: '',
};

const initialState = {
  showDatePicker: false,
  mealPlanHintEnabled: true,
  orderDetails: undefined, // used for storing temporarily the order details data to navigate on next screen
  orderDetailsFilter: undefined, // use for storing the current filter where the order is found (use for getting the order details to rtk cache)
  gettingUserData: {
    details: gettingUserDataInitialState,
    address: gettingUserDataInitialState,
    cart: gettingUserDataInitialState,
  },
  settings: {
    savedPayment: [
      { native: true, name: constants.isIOS ? 'Apple Pay' : 'Google Pay' },
    ],
    notification: {
      all: true,
      orders: true,
      promotion: false,
      sms: false,
    },
  },
  chooseDifferentPaymentMethod: false, // used on the checkout when event "payment-failed" is received and upon user clicked the choose different payment method
  rescheduleStoreId: '',
  // for order banner
  hasOrderBanner: false,
  isOrderBannerMinimized: false,
  // for basket tab
  highlightOrderId: '', // order_id or "first"
  scrollCartToStoreId: '',
  // value e.g: { id: "store id", orderId: "order id" }
  successfulOrder: [], // storing of store id and order id successful order to show to this id only the verifying payment
  loadAllStoresAndActiveOrders: false, // to control when to load the all stores & active orders (should load first the funnels)
  reservationCheckoutData: null, // typeof ReservationCheckout from Screens/ReservationCheckout/types
  route: { name: '', params: undefined }, // for global tracking of routes since on web useRoute hooks is not always available
};

export const utilitySlice = createSlice({
  name: 'utility',
  initialState,
  reducers: {
    addPaymentMethod: (state, action) => {
      state.settings.savedPayment = [
        ...state.settings.savedPayment,
        action.payload,
      ];
    },
    addSuccessfulOrder: (state, action) => {
      const storeId = action.payload?.replace?.('_event', ''); // action.payload is store id on event store id has suffix _event on the id
      if (lodashIsUndefined(state.successfulOrder)) {
        // if utility redux is already has persisting value, successfulOrder is usually undefined even we set initial value for it
        state.successfulOrder = []; // set initial value
      }
      const existingIndex = lodashFindIndex(
        state.successfulOrder,
        (e) => e.storeId === storeId,
      );
      if (existingIndex === -1) {
        // if existingIndex is -1 it means, no existing successful order on that order id
        state.successfulOrder.push({ id: storeId });
      }
    },
    setLoadAllStoresAndActiveOrders: (state, action) => {
      state.loadAllStoresAndActiveOrders = action.payload;
    },
    removeSuccessfulOrder: (state, action) => {
      const storeId = action.payload?.replace?.('_event', ''); // action.payload is store id on event store id has suffix _event on the id
      const successfulOrderIndex = lodashFindIndex(
        state.successfulOrder,
        (e) => e.id === storeId,
      );
      state.successfulOrder.splice(successfulOrderIndex, 1);
    },
    setChooseDifferentMethod: (state, action) => {
      state.chooseDifferentPaymentMethod = action.payload; // true or false
    },
    setReservationCheckoutAddOn: (state, action) => {
      state.reservationCheckoutData[formikNames.ADDONS] = action.payload;
    },
    setReservationCheckoutData: (state, action) => {
      state.reservationCheckoutData = action.payload; // shape is depending on the data need on experience checkout
    },
    setMealPlanHintEnabled: (state, action) => {
      state.mealPlanHintEnabled = action.payload; // boolean
    },
    setGettingUserData: (state, action) => {
      // key = string from gettingUserData key on initial state or undefined
      // value = object from gettingUserDataInitialState or undefined
      const key = action?.payload?.key;
      const value = action?.payload?.value || {};
      if (key && !lodashIsEmpty(value)) {
        // set key to value
        state.gettingUserData[key] = {
          ...gettingUserDataInitialState,
          ...value,
        };
      } else {
        // set all keys to:
        Object.keys(state.gettingUserData).forEach((e) => {
          if (lodashIsEmpty(value)) {
            // loading state to true
            state.gettingUserData[e] = {
              ...gettingUserDataInitialState,
              loading: true,
            };
          } else {
            // or the user defined value
            state.gettingUserData[e] = {
              ...gettingUserDataInitialState,
              ...value,
            };
          }
        });
      }
    },
    setHasOrderBanner: (state, action) => {
      state.hasOrderBanner = action.payload || false;
    },
    setHighlightOderId: (state, action) => {
      state.highlightOrderId = action.payload || '';
    },
    setOrderBannerMinimized: (state, action) => {
      state.isOrderBannerMinimized = action.payload;
    },
    setOrderDetails: (state, action) => {
      state.orderDetails = action.payload;
    },
    setOrderDetailsFilter: (state, action) => {
      state.orderDetailsFilter = action.payload;
    },
    setPaymentMethodAsDefault: (state, action) => {
      const prevDefaultIndex = lodashFindIndex(state.settings.savedPayment, {
        default: true,
      });
      const toBeSetAsDefaultIndex = lodashFindIndex(
        state.settings.savedPayment,
        action.payload,
      );
      if (prevDefaultIndex !== toBeSetAsDefaultIndex) {
        if (prevDefaultIndex >= 0) {
          // if greater than equal to 0 meaning it found the index on the array of saved payment
          // less than 0 is expected to be -1 it means user doesnt have selected default payment method yet
          delete state.settings.savedPayment[prevDefaultIndex].default;
        }
        state.settings.savedPayment[toBeSetAsDefaultIndex].default = true;
      }
      // do nothing if prevDefaultIndex and toBeSetAsDefaultIndex is equal,
      // meaning user is clicking the already selected as default
    },
    setRescheduleStoreId: (state, action) => {
      state.rescheduleStoreId = action.payload || '';
    },
    setScrollCartToStoreId: (state, action) => {
      state.scrollCartToStoreId = action.payload;
    },
    setRoute: (state, action) => {
      state.route = action.payload || { name: '', params: undefined };
    },
    toggleDatePicker: (state) => {
      state.showDatePicker = !state.showDatePicker;
    },
    updateNotificationSetting: (state, action) => {
      const { key, value } = action.payload;
      state.settings.notification[key] = value;
    },
    updateSuccessfulOrder: (state, action) => {
      const { storeId, orderId } = action.payload;
      const successOrderIndex = lodashFindIndex(
        state.successfulOrder,
        (e) => e.id === storeId && typeof e.orderId === 'undefined',
      );
      if (successOrderIndex !== -1) {
        // it should always goes here when dispatched this action
        state.successfulOrder[successOrderIndex].orderId = orderId;
      }
    },
  },
});

export const {
  addPaymentMethod,
  addSuccessfulOrder,
  removeSuccessfulOrder,
  setChooseDifferentMethod,
  setReservationCheckoutAddOn,
  setReservationCheckoutData,
  setMealPlanHintEnabled,
  setGettingUserData,
  setHasOrderBanner,
  setHighlightOderId,
  setLoadAllStoresAndActiveOrders,
  setOrderBannerMinimized,
  setOrderDetails,
  setOrderDetailsFilter,
  setPaymentMethodAsDefault,
  setRescheduleStoreId,
  setScrollCartToStoreId,
  setRoute,
  toggleDatePicker,
  updateNotificationSetting,
  updateSuccessfulOrder,
} = utilitySlice.actions;
export default utilitySlice.reducer;
